'use client'
import {useState, useEffect, useRef, useMemo, useContext} from 'react'
import {Button, ExperiencesLike} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {BookMenu} from './BookMenu'
import {StayContext} from '@local/stores/contexts'
import {HotelEnquireSlideout} from './HotelEnquireSlideout'
import {event} from '@local/utils/gtm'

type Room = {
	name: string;
	title: string;
	description: string;
	packages: Array<{
		name: string;
		price: number;
	}>;
};

type BookingProps = {
	rooms: Array<Room>;
	like: Array<Room>;
	hotelEnquireQuestions: any;
};

export const Booking = ({
	rooms,
	like,
	hotelEnquireQuestions,
}: BookingProps) => {
	const mobileMenuRef = useRef<HTMLDivElement>(null)
	const {room, date, guests, roomPackage, mobileMenuOpen, setMobileMenuOpen} =
		useContext(StayContext)
	const [isOpen, setIsOpen] = useState<boolean>(false)

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				mobileMenuOpen &&
				mobileMenuRef.current &&
				!mobileMenuRef.current.contains(event.target as Node)
			) {
				setMobileMenuOpen(false)
			}
		}
		if (mobileMenuOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [mobileMenuOpen, setMobileMenuOpen])

	const startingAt = useMemo(() => {
		if (roomPackage) {
			const selectedRoom = rooms.find((r) => r.name === room)
			const selectedPackage = selectedRoom?.packages.find(
				(p) => p._key === roomPackage,
			)
			return selectedPackage?.startingPrice
		} else {
			if (room) {
				const selectedRoom = rooms.find((r) => r.name === room)
				const prices =
					selectedRoom?.packages?.map((p) => p.startingPrice) || []
				return prices.length > 0 ? Math.min(...prices) : null
			} else {
				const allPrices = rooms
					?.flatMap((room) => room?.packages?.map((pkg) => pkg.startingPrice))
					.filter((price) => price !== undefined && price !== null)

				return allPrices.length > 0 ? Math.min(...allPrices) : null
			}
		}
	}, [rooms, room, roomPackage])

	return (
		<div>
			<div className='bg-grey-1 hidden lg:block p-20 w-full h-fit lg:sticky  lg:top-120'>
				<div className='flex flex-col gap-y-20'>
					<BookMenu rooms={rooms} />
					{startingAt && (
						<div className='flex flex-row justify-between items-center'>
							<span className='text-12 font-medium leading-none text-black uppercase'>
								Starting at
							</span>
							<span className='text-18 font-350 text-dark'>{startingAt} €</span>
						</div>
					)}
					<Button
						small
						variant='primary'
						className='disabled:bg-grey-3 disabled:text-gray disabled:cursor-not-allowed'
						disabled={!guests || !date || !room}
						onClick={() => setIsOpen(true)}
						label='ENQUIRE NOW' />
				</div>
			</div>
			<ExperiencesLike experiences={like} />
			<div
				className='fixed bottom-0 left-0 right-0 lg:hidden'
				ref={mobileMenuRef}>
				<div
					className={cx(
						'transition-all duration-300 bg-grey-1 py-10 px-10 h-0 opacity-0 -z-50',
						mobileMenuOpen && 'h-fit opacity-100',
					)}>
					<BookMenu rooms={rooms} />
				</div>
				<div className='bg-grey-1 py-14 px-10 w-full z-1 lg:hidden'>
					{startingAt
						? (
							<div className='flex flex-row justify-between items-center mb-10'>
								<span className='text-12 font-medium leading-none text-black uppercase'>
									Starting At
								</span>
								<span className='text-18 font-[350] text-dark'>
									{startingAt} €
								</span>
							</div>
						)
						: null}
					<div className='flex flex-row justify-between items-center'>
						{mobileMenuOpen
							? (
								<Button
									small
									className='w-full'
									variant='primary'
									onClick={() => {
										event('add_to_cart', {
											ecommerce: {
												currency: 'EUR',
												value: startingAt || 0,
												items: [{
													item_name: room,
												}],
											},
										})
										setIsOpen(true)
									}}
									label='ENQUIRE NOW' />
							)
							: (
								<Button
									label='Select Options'
									small
									className='w-full'
									variant='primary'
									onClick={() => setMobileMenuOpen(true)} />
							)}
					</div>
				</div>
			</div>

			<HotelEnquireSlideout
				isOpen={isOpen}
				onClickClose={() => setIsOpen(false)}
				hotelEnquireQuestions={hotelEnquireQuestions}
				reservation={{
					arrivalDate: date?.[0] === null ? '' : new Date(date?.[0]),
					departureDate: date?.[1] === null ? '' : new Date(date?.[1]),
					guestCounts: guests || '',
					stay: `${room} - ${roomPackage}`,
				}} />
		</div>
	)
}
